/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography, Stack, TextField, Skeleton, IconButton } from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { navigationSettingsState, navigationTranslationState, sliderValueState } from 'store';
import { Loader } from '@treedis/tour-loader';
import { FilterMenu } from './components/FilterMenu';
import { SortMenu } from './components/SortMenu';
import { FinishModal } from './components/FinishModal';
import { TourType } from '@/_models/Tour';
import { createInputProps, renderIcon, trimDescription as trimText } from './util';
import DeviceManager from '@/components/util/deviceManager';
import { NavigationCounterWrapper } from './components/NavigationCounterWrapper';
import { convertDestinationItemToSweep, getQueryVariable, sendToGA } from '@/components/util';
import { SkeletonPathItem } from '@/components/navigation/NavigationIntroScreen/components/SkeletonPathItem';
import { NavigationIntroPopup } from '@/components/navigation/NavigationIntroScreen/components/Popup/NavigationIntroPopup';
import { NavigationIntroChoicePopup } from '@/components/navigation/NavigationIntroScreen/components/Popup/NavigationIntroChoicePopup';
import { mobileCloseButtonStyle, useSearchbarStyles } from './styles';
import { NavItemsSlider } from './components/NavItemsSlider';
import {
  ChevronLeftIcon,
  CrossIcon,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { NavigationSharePopup } from '@/components/navigation/NavigationIntroScreen/components/Popup/NavigationSharePopup';
import { NavigationTourSharePopup } from '@/components/navigation/NavigationIntroScreen/components/Popup/NavigationTourSharePopup';
import { NavigationSearchBarProps } from './types';

export const NavigationSearchBar = ({
  setDestination,
  menuItems,
  isBackgroundVisible,
  setBackgroundVisible,
  actionPathFinished,
  tour,
  target,
  categories,
  moveToStartingPoint,
  filtersEnabled,
  sortingEnabled,
  defaultSorting,
  navigationFinishScreen,
  navigationStations,
  tourType,
  selectedCategory,
  selectCategory,
  appState,
  setActionPathFinished,
  isSearchBarFocused,
  setIsSearchBarFocused,
  rtl,
  inAutoNavigate,
  kioskIsReset,
  setKioskIsReset,
  searchText,
  setSearchText,
  hiddenFromLink,
  showSkeleton,
  pathFinderLine,
  setDescriptionEnabled,
  mobilePosition,
  setMobilePosition,
  popupOpen,
  setPopupOpen,
  setPopupHeight,
  startingPoint,
  allSweeps,
  onSetStartingPoint,
  currentSweep,
  setNavigationSharingPopupOpen,
  navigationSharingPopupOpen,
  detailedRoutes,
  choicePopupOpen,
  setChoicePopupOpen,
  showDetailedRoutesOnly,
  closeDetailedRoutesList,
  setActionPathNavbarPlayButtonType,
  resetDetailedRoutes,
  allTags,
  teleportPoints,
  navType,
  setNavType,
  selectedItem,
  setSelectedItem,
  setStartingPoint,
  resetNavigationByCloseButton,
  shouldNavReset,
  showCaseTemp,
}: NavigationSearchBarProps) => {
  const {
    sharingEnabled,
    popupTitle,
    qrEnabled,
    qrTitle,
    smsEnabled,
    smsTitle,
    smsText,
    policy,
    country,
  } = navigationFinishScreen;
  const inputRef = useRef<HTMLInputElement>(null);
  const ratingBoardRef = useRef(null);
  const [inputClientRect, setInputClientRect] = useState(null);
  const [ratingClientRect, setRatingClientRect] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isRatingBoardVisible, setIsRatingBoardVisible] = useState(false);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<number[]>([]);
  const [sortingValue, setSortingValue] = useState('default');
  const [startSweep, setStartSweep] = useState(null);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const navigationSettings = useRecoilValue(navigationSettingsState);
  const setSliderValue = useSetRecoilState(sliderValueState);
  const translation = useRecoilValue(navigationTranslationState);
  const [popupContentLoaded, setPopupContentLoaded] = useState(false);
  const [choicePopupContentLoaded, setChoicePopupContentLoaded] = useState(false);
  const [choiceButtonLoadingProgress, setChoiceButtonLoadingProgress] = useState(0);
  const [startPointId, setStartPointId] = useState(null);
  const choiceButtonLoadingProgressRef = useRef<any>();
  const appStateRef = useRef<any>(appState);
  const intervalIdRef = useRef<any>();
  const startPointTitleRef = useRef('');
  const finishTitleRef = useRef('');

  const combineNavigationItems = useMemo(() => {
    if (tourType === TourType.Kiosk) {
      const foundStation = navigationStations?.find((kiosk) => kiosk?.url.includes(startSweep?.id));
      if (foundStation) {
        const filteredStations = navigationStations?.filter(
          (kiosk) => kiosk.url !== foundStation.url,
        );
        const array = [foundStation, ...filteredStations];
        return array;
      }
    }
    const combineArray = [startSweep, ...navigationStations];
    return combineArray;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationStations, startSweep]);

  const filteredItems = useMemo(() => {
    const searchTextLower = searchText.toLowerCase();

    const filtered = combineNavigationItems?.concat(menuItems)?.filter((item) => {
      const titleMatch =
        item?.[item?.type]?.title?.toLowerCase()?.includes(searchTextLower) ?? false;
      const keywordMatch = item?.tag?.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchTextLower),
      );
      const descriptionMatch = item?.tag?.description?.toLowerCase().includes(searchTextLower);
      const filterMatch = item?.filters?.some((filter) =>
        filter?.title?.toLowerCase().includes(searchTextLower),
      );
      const titleKioskMatch = item?.title?.toLowerCase().includes(searchTextLower);
      const titleSweepMatch =
        item?.title?.toLowerCase().includes(searchTextLower) ||
        item?.name?.toLowerCase().includes(searchTextLower);

      const matchesSearchText =
        titleMatch ||
        keywordMatch ||
        filterMatch ||
        descriptionMatch ||
        titleKioskMatch ||
        titleSweepMatch;

      if (selectedCategory && selectedFilters.length) {
        return (
          item?.categoryId === selectedCategory?.id &&
          matchesSearchText &&
          item?.filters?.some((filter) => selectedFilters.includes(filter.id))
        );
      }

      if (choicePopupOpen) {
        selectCategory(null);
      }

      if (selectedCategory) {
        return item?.categoryId === selectedCategory?.id && matchesSearchText;
      }

      if (selectedFilters.length) {
        return (
          matchesSearchText && item?.filters?.some((filter) => selectedFilters.includes(filter.id))
        );
      }

      return matchesSearchText;
    });

    return filtered.sort((a, b) => {
      // Alphabetical sorting
      if (sortingValue === 'alphabetical') {
        return (
          a[a.type]?.title?.localeCompare(b[b.type]?.title) ||
          a?.title?.localeCompare(b?.title) ||
          a?.name?.localeCompare(b?.name)
        );
      }
      // Reverse alphabetical sorting
      if (sortingValue === 'reverseAlphabetical') {
        return (
          b[b.type]?.title?.localeCompare(a[a.type]?.title) ||
          b?.title?.localeCompare(a?.title) ||
          b?.name?.localeCompare(a?.name)
        );
      }

      // No sorting for 'default'
      return a.order - b.order;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchText,
    combineNavigationItems,
    menuItems,
    selectedCategory,
    selectedFilters,
    choicePopupOpen,
    sortingValue,
  ]);

  const wrapperWidth = useMemo(() => {
    if (DeviceManager.isMobile()) {
      return '100%';
    }

    return '1138px';
  }, []);

  const setNavigationDestination = (item: any, navigationType: any, preLoad = false) => {
    resetDetailedRoutes();
    setSliderValue(0);

    setKioskIsReset(false);
    // setHiddenFromLink(false);
    setDestination({
      navigationSettings,
      setSliderValue,
      navigationType,
      preLoad,
      finishingLocation: item,
      startingLocation: startingPoint,
    });
  };

  const processItem = (item, navigationType) => {
    setShowLoader(false);
    setIsSearchBarFocused(false);
    setShowModal(false);
    const itemTitle = item?.title || item?.name || item[item.type]?.title;
    setSearchText(itemTitle);
    setIsRatingBoardVisible(false);
    setActionPathFinished(false);
    setLoadingFinished(false);
    setSliderValue(0);
    setPopupOpen(false);

    if (item?.type === 'tag') {
      sendToGA('event', {
        eventCategory: 'Mattertag',
        eventAction: 'click',
        eventLabel: item?.tag?.title,
        eventType: item?.tag?.type,
      });
    }

    setNavigationDestination(item, navigationType);
  };

  const handleItemSelect = (item: any) => {
    setSelectedItem({ ...item, descriptionEnabled: navigationSettings.descriptionEnabled });
    setPopupOpen(true);
    setActionPathFinished(false);

    if (appState === 'playing') {
      setPopupContentLoaded(true);
    } else
      setTimeout(() => {
        setPopupContentLoaded(true);
      }, 2000);
  };

  const handleItemConfirmed = async (item: any, navigationType: any) => {
    processItem(item, navigationType);
  };

  const resetNavigation = (caller?: string) => {
    setSearchText('');
    setShowModal(false);
    setIsRatingBoardVisible(false);
    setBackgroundVisible(caller !== 'fromRegularTour');
    setIsSearchBarFocused(caller !== 'fromRegularTour');
    moveToStartingPoint();
    if (caller === 'fromKioskTimer') {
      setKioskIsReset(true);
      selectCategory(null);
      setSelectedItem(null);
      setPopupOpen(false);
      setChoicePopupOpen(false);
      setSharePopupOpen(false);
      setNavigationSharingPopupOpen(false);
    }
  };

  const clearSearch = (event) => {
    event.stopPropagation();
    setSearchText('');
    if (selectedCategory) {
      selectCategory(null);
    }
    if (selectedItem) {
      setSelectedItem(null);
    }
    setBackgroundVisible(true);
    setShowModal(false);
    setIsRatingBoardVisible(false);
    setIsSearchBarFocused(false);
  };

  useEffect(() => {
    if (defaultSorting) {
      setSortingValue(defaultSorting);
    }
  }, [defaultSorting]);

  useEffect(() => {
    if (isRatingBoardVisible) {
      setRatingClientRect(ratingBoardRef?.current?.getBoundingClientRect());
    }
  }, [isRatingBoardVisible]);

  useEffect(() => {
    if (inputRef.current) {
      setInputClientRect(inputRef.current.getBoundingClientRect());
    }
  }, [isBackgroundVisible]);

  useEffect(() => {
    // Check if the search bar should be focused and if the ref is attached to an element
    if (isSearchBarFocused && inputRef.current && mobilePosition < 2) {
      inputRef.current.blur();
    }
  }, [isSearchBarFocused, mobilePosition]);

  useEffect(() => {
    if (isBackgroundVisible) {
      setShowModal(false);
      setIsRatingBoardVisible(false);
    }
  }, [isBackgroundVisible]);

  useEffect(() => {
    if (loadingFinished && (tourType === TourType.Kiosk || tourType === TourType.Regular)) {
      setShowModal(true);
      if (tourType === TourType.Regular) resetNavigation('fromRegularTour');
    }
    if (loadingFinished && tourType === TourType.Kiosk && !sharingEnabled) {
      resetNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFinished, tourType, sharingEnabled]);

  useEffect(() => {
    if ((selectedCategory || categories.length === 1) && !DeviceManager.isMobile()) {
      setIsSearchBarFocused(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, categories]);

  useEffect(() => {
    if (choicePopupOpen) {
      if (appState !== 'playing') {
        setTimeout(() => {
          setChoicePopupContentLoaded(true);
        }, 2000);

        const targetProgress = 85;
        const duration = 50000;
        const startTime = Date.now();
        // eslint-disable-next-line no-restricted-properties
        const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
        if (!intervalIdRef.current) {
          intervalIdRef.current = setInterval(() => {
            if (
              appStateRef.current === 'playing' ||
              choiceButtonLoadingProgressRef.current === 100
            ) {
              setChoiceButtonLoadingProgress(100);
              clearInterval(intervalIdRef.current);
              return;
            }

            const elapsedTime = Date.now() - startTime;
            const normalizedTime = Math.min(elapsedTime / duration, 1);
            const easedProgress = easeOutExpo(normalizedTime) * targetProgress;

            const newProgress = Math.floor(easedProgress);
            setChoiceButtonLoadingProgress(newProgress);
          }, 100);
        }
      } else {
        setChoicePopupContentLoaded(true);
        setChoiceButtonLoadingProgress(100);
      }
    }
  }, [choicePopupOpen, appState]);

  useEffect(() => {
    choiceButtonLoadingProgressRef.current = choiceButtonLoadingProgress;
  }, [choiceButtonLoadingProgress]);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    const targetQueryParam = getQueryVariable(window.location.href, 'target');
    const startQueryParam = getQueryVariable(window.location.href, 'start');

    // // this is for loading in mobile from qr or sms link
    if (
      !target &&
      targetQueryParam &&
      startQueryParam &&
      tourType === TourType.Regular &&
      !showSkeleton &&
      allSweeps &&
      allTags
    ) {
      const targetTag = filteredItems.find((item) => item.tagSid === targetQueryParam);
      const targetStation = navigationStations.find(
        (station) => Number(targetQueryParam) === station?.id,
      );
      const targetSweep: any = Array.from(allSweeps?.values()).find(
        (s: any) => s.sweep_uuid === targetQueryParam,
      );

      const startTag = filteredItems.find((item) => {
        const foundTag: any = Array.from(allTags?.values()).find(
          (tag: any) => tag.sid === startQueryParam || tag.defaultSid === startQueryParam,
        );
        return foundTag
          ? foundTag.sid === item.tagSid || foundTag.defaultSid === item.tagSid
          : null;
      });

      const startStation = filteredItems.find((item) => {
        const foundStation = navigationStations.find(
          (station) => Number(startQueryParam) === station?.id,
        );
        return foundStation ? foundStation.id === item.id : null;
      });

      const startingSweep: any = startQueryParam
        ? Array.from(allSweeps?.values()).find((s: any) => s.sweep_uuid === startQueryParam)
        : Array.from(allSweeps.values()).find(
            (s: any) => s.sweep_uuid === currentSweep.uuid || s.id === currentSweep.id,
          );

      const newTarget = targetTag || targetStation || targetSweep;
      const newStart = startTag || startStation || startingSweep;
      const startingItem = convertDestinationItemToSweep({
        item: newStart,
        allSweeps: Array.from(allSweeps.values()),
        allTags: Array.from(allTags.values()),
      });

      handleItemSelect(newTarget);
      setStartingPoint(startingItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState, target, pathFinderLine, showSkeleton]);

  useEffect(() => {
    setDescriptionEnabled(navigationSettings.descriptionEnabled);
  }, [navigationSettings, setDescriptionEnabled]);

  const {
    searchWrapperStyle,
    searchTitleWrapperStyle,
    menuButtonStyle,
    clearSearchButtonStyle,
    searchTextFieldStyle,
    resultsMainWrapperStyle,
    resultsSecondWrapperStyle,
    chevronWrapperStyle,
    itemsWrapperStyle,
    itemStyle,
    itemIconStyle,
    itemChevronStyle,
    itemInnerStyle,
    choicePopupWrapper,
    choicePopupSearchBar,
    choicePopupFilteredBtn,
    popupsContainerStyle,
  } = useSearchbarStyles({
    showSkeleton,
    wrapperWidth,
    isBackgroundVisible,
    mobilePosition,
    popupOpen,
    filteredItems,
    rtl,
    searchText,
    selectedCategory,
    navigationSharingPopupOpen,
    actionPathFinished,
  });

  useEffect(() => {
    if (!startPointId) {
      setStartPointId(currentSweep);
    }
  }, [tour.id, currentSweep, startPointId]);

  useEffect(() => {
    if (startPointId && allSweeps) {
      const foundSweep = Array.from(allSweeps?.values()).find(
        (s: any) => s.sweep_uuid === startPointId?.uuid || s.sweepId === startPointId?.id,
      );
      setStartSweep(foundSweep);
    }
  }, [allSweeps, startPointId, tour?.ss]);

  const handleReplay = async () => {
    setNavigationDestination(target, navType);
    setActionPathFinished(false);
  };

  startPointTitleRef.current = useMemo(() => {
    return (
      startingPoint?.tag?.title ||
      startingPoint?.station?.title ||
      startingPoint?.title ||
      startingPoint?.name ||
      'Title'
    );
  }, [startingPoint]);

  finishTitleRef.current = useMemo(() => {
    const finishItem = selectedItem || target;

    return (
      finishItem?.tag?.title ||
      finishItem?.station?.title ||
      finishItem?.title ||
      finishItem?.name ||
      'Title'
    );
  }, [target, selectedItem]);

  const handleBackToSearchBar = () => {
    setPopupOpen(false);
    setChoicePopupOpen(false);
    setSharePopupOpen(false);
    setNavigationSharingPopupOpen(false);
    setBackgroundVisible(true);
    setSearchText('');
    setSelectedItem(null);
  };

  const handleCloseOrBack = () => {
    selectCategory(null);
    setIsSearchBarFocused(false);
    setSearchText('');
    setMobilePosition(1);
    if (selectedItem) {
      setSelectedItem(null);
    }
    if (popupOpen) {
      setPopupOpen(false);
    }
  };

  useEffect(() => {
    if (shouldNavReset) {
      handleBackToSearchBar();
      resetNavigationByCloseButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldNavReset]);

  return (
    <Stack sx={{ width: { xs: '100%', sm: '100vw' } }}>
      {DeviceManager.isMobile() &&
        // !actionPathFinished &&
        ((mobilePosition > 1 &&
          !choicePopupOpen &&
          !showDetailedRoutesOnly &&
          !popupOpen &&
          (!!searchText || !!selectedCategory)) ||
          popupOpen) && (
          <Button sx={mobileCloseButtonStyle} onClick={handleCloseOrBack}>
            <CrossIcon style={{}} />
          </Button>
        )}
      {(popupOpen ||
        (choicePopupOpen && startSweep) ||
        sharePopupOpen ||
        navigationSharingPopupOpen) && (
        <Stack id="popups-container" sx={popupsContainerStyle}>
          {popupOpen && (
            <NavigationIntroPopup
              open={popupOpen}
              handleBack={() => {
                setPopupOpen(false);
                setSelectedItem(null);
              }}
              item={selectedItem}
              contentLoaded={popupContentLoaded}
              rtl={rtl}
              onConfirm={() => {
                setPopupOpen(false);
                setChoicePopupOpen(true);
                setMobilePosition(1);
              }}
              tour={tour}
              setPopupHeight={setPopupHeight}
              setSharePopupOpen={setSharePopupOpen}
              sharePopupOpen={sharePopupOpen}
              sharingEnabled={sharingEnabled}
              mobilePosition={mobilePosition}
            />
          )}
          {choicePopupOpen && startSweep && (
            <NavigationIntroChoicePopup
              open={choicePopupOpen}
              setOpen={setChoicePopupOpen}
              item={selectedItem}
              contentLoaded={choicePopupContentLoaded}
              loadingProgress={choiceButtonLoadingProgress}
              rtl={rtl}
              onConfirm={(navigationType) => {
                setChoicePopupOpen(false);
                handleItemConfirmed(selectedItem, navigationType);
                setNavType(navigationType);
              }}
              onReturn={() => {
                setPopupOpen(true);
              }}
              startingPoint={startingPoint}
              onSetStartingPoint={(item) => {
                onSetStartingPoint(item);
              }}
              onSetFinishPoint={(finishItem: any) => {
                resetDetailedRoutes();
                setSelectedItem({
                  ...finishItem,
                  descriptionEnabled: navigationSettings.descriptionEnabled,
                });
              }}
              menuItems={menuItems}
              tour={tour}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              startSweep={startSweep}
              choicePopupWrapper={choicePopupWrapper}
              choicePopupSearchBar={choicePopupSearchBar}
              choicePopupFilteredBtn={choicePopupFilteredBtn}
              sharingEnabled={sharingEnabled}
              setSharePopupOpen={setSharePopupOpen}
              sharePopupOpen={sharePopupOpen}
              setOpenNavigationSharingPopup={setNavigationSharingPopupOpen}
              setNavigationDestination={setNavigationDestination}
              target={target}
              detailedRoutes={detailedRoutes}
              allSweeps={allSweeps}
              startingPointTitle={startPointTitleRef.current}
              finishPointTitle={finishTitleRef.current}
              allTags={allTags}
              filteredItems={filteredItems}
              searchText={searchText}
              setSearchText={setSearchText}
              setSortingValue={setSortingValue}
              mobilePosition={mobilePosition}
              navigationType={navType}
              teleportPoints={teleportPoints}
              selectedNavigationType={navType}
              setSelectedNavigationType={setNavType}
              setSelectedItem={setSelectedItem}
              setMobilePosition={setMobilePosition}
              tourType={tourType}
            />
          )}
          {sharePopupOpen && (
            <NavigationSharePopup
              open={sharePopupOpen}
              setOpen={setSharePopupOpen}
              setPopupHeight={setPopupHeight}
              popupTitle={popupTitle}
              qrEnabled={qrEnabled}
              qrTitle={qrTitle}
              smsEnabled={smsEnabled}
              smsTitle={smsTitle}
              smsText={smsText}
              target={selectedItem || target}
              startingPoint={startingPoint}
              rtl={rtl}
              country={country}
              policy={policy}
            />
          )}
          {navigationSharingPopupOpen && (
            <Stack
              id="tour-share-wrapper"
              sx={{
                ...searchWrapperStyle,
                width: { xs: '100%', sm: 'fit-content' },
                marginLeft: 0,
                top: 0,
                left: 0,
                position: 'absolute',
              }}
            >
              <>
                <NavigationTourSharePopup
                  open={navigationSharingPopupOpen}
                  setOpen={setNavigationSharingPopupOpen}
                  setPopupHeight={setPopupHeight}
                  popupTitle={popupTitle}
                  qrEnabled={qrEnabled}
                  qrTitle={qrTitle}
                  smsEnabled={smsEnabled}
                  smsTitle={smsTitle}
                  smsText={smsText}
                  target={selectedItem || target}
                  startingPoint={startingPoint}
                  setBackgroundVisible={setBackgroundVisible}
                  setChoicePopupOpen={setChoicePopupOpen}
                  setNavigationDestination={setNavigationDestination}
                  item={selectedItem}
                  detailedRouteEnabled={navigationSettings.detailedRouteEnabled}
                  detailedRoutes={detailedRoutes}
                  setActionPathNavbarPlayButtonType={setActionPathNavbarPlayButtonType}
                  navigationManager={pathFinderLine}
                  navigationType={navType}
                  actionPathFinished={actionPathFinished}
                  handleBackToSearchBar={handleBackToSearchBar}
                  handleReplay={handleReplay}
                  tour={tour}
                  ratingEnabled={navigationFinishScreen.ratingEnabled}
                  startingPointTitle={startPointTitleRef.current}
                  finishPointTitle={finishTitleRef.current}
                  closeDetailedRoutesList={closeDetailedRoutesList}
                  showDetailedRoutesOnly={showDetailedRoutesOnly}
                  rtl={rtl}
                  country={country}
                  showCaseTemp={showCaseTemp}
                  policy={policy}
                />
              </>
            </Stack>
          )}
        </Stack>
      )}

      {!popupOpen && !choicePopupOpen && !sharePopupOpen && !navigationSharingPopupOpen && (
        <Stack id="search-wrapper" sx={searchWrapperStyle}>
          <>
            {!!isBackgroundVisible && DeviceManager.isMobile() && (
              <Stack id="title-wrapper" sx={searchTitleWrapperStyle}>
                {!!target && !kioskIsReset && (
                  <Button
                    sx={menuButtonStyle}
                    variant="contained"
                    onClick={() => {
                      setSearchText('');
                      if (categories?.length > 1) selectCategory(null);
                      setIsSearchBarFocused(true);
                    }}
                  >
                    <i
                      className="fa-solid fa-bars"
                      style={{
                        color: '#000',
                      }}
                    />
                  </Button>
                )}
                <Typography
                  fontSize={{ xs: '20px', lg: '32px' }}
                  mx="auto"
                  px={{ xs: '32px', sm: 'unset' }}
                  fontWeight={600}
                  textAlign="center"
                  sx={{
                    display: mobilePosition > 1 && searchText.length > 0 ? 'none' : 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    wordBreak: 'break-word',
                    maxHeight: '48px',
                    lineHeight: '24px',
                    direction: rtl ? 'rtl' : 'ltr',
                  }}
                >
                  {navigationSettings?.titleEnabled && navigationSettings?.title
                    ? navigationSettings?.title
                    : ''}
                </Typography>
                <Typography
                  fontSize={{ xs: '20px', lg: '32px' }}
                  mx="auto"
                  fontWeight={{ xs: 500, lg: 600 }}
                  textAlign="center"
                  sx={{
                    display: mobilePosition > 1 && searchText.length > 0 ? 'block' : 'none',
                    direction: rtl ? 'rtl' : 'ltr',
                    color: '#000000',
                  }}
                >
                  {translation?.navDirections || 'Directions'}
                </Typography>
                {!!target && !kioskIsReset && (
                  <Button
                    sx={clearSearchButtonStyle}
                    variant="contained"
                    onClick={() => setBackgroundVisible(false)}
                  >
                    <i
                      className="fa-solid fa-xmark"
                      style={{
                        color: '#000',
                      }}
                    />
                  </Button>
                )}
              </Stack>
            )}
            {showSkeleton ? (
              <Skeleton
                variant="rounded"
                height={76}
                sx={{ marginTop: '21px', marginBottom: '17px' }}
              />
            ) : (
              <TextField
                disabled={false}
                autoComplete="off"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                inputRef={inputRef}
                InputProps={{
                  ...createInputProps({
                    clearSearch,
                    isSearchBarFocused,
                    actionPathFinished,
                    loadingFinished,
                  }),
                  readOnly: DeviceManager.isMobile() && mobilePosition < 2 && !isSearchBarFocused,
                }}
                sx={{ ...searchTextFieldStyle, direction: rtl ? 'rtl' : '' }}
                placeholder={translation?.navSearchingQuestion || 'What are you searching for?'}
                onClick={(event) => {
                  if (DeviceManager.isMobile()) {
                    if (mobilePosition < 2) {
                      event.preventDefault();
                      inputRef.current.blur();
                    }
                    setMobilePosition(2);
                  }
                  if (!isBackgroundVisible) {
                    setBackgroundVisible(true);
                  }
                  setIsSearchBarFocused(true);
                }}
              />
            )}
          </>
        </Stack>
      )}

      {!popupOpen &&
        !choicePopupOpen &&
        !navigationSharingPopupOpen &&
        (hiddenFromLink ||
          (showSkeleton && !categories?.length) ||
          ((!!searchText.length || !!selectedCategory || !categories.length) &&
            isBackgroundVisible)) && (
          <Stack id="results-wrapper" sx={resultsMainWrapperStyle}>
            <Stack
              id="results-wrapper-2"
              sx={{ ...resultsSecondWrapperStyle, direction: rtl ? 'rtl' : '' }}
            >
              <Stack direction="row" gap={1} alignItems="center">
                {DeviceManager.isMobile() && (
                  <IconButton
                    sx={{
                      borderRadius: '1rem',
                      padding: '4px',
                      background: '#EAECF0',
                      width: '32px',
                      height: '32px',
                    }}
                    onClick={handleCloseOrBack}
                  >
                    <ChevronLeftIcon style={{}} />
                  </IconButton>
                )}
                {selectedCategory && (
                  <Stack
                    sx={chevronWrapperStyle}
                    onClick={() => {
                      selectCategory(null);
                      setSelectedItem(null);
                      setIsSearchBarFocused(false);
                      setSearchText('');
                    }}
                  >
                    <i
                      className={`fa-solid fa-chevron-${rtl ? 'right' : 'left'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </Stack>
                )}
                <Typography
                  fontSize="16px"
                  fontWeight={rtl ? 400 : 500}
                  sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                >
                  {categories.length === 1 && !searchText
                    ? translation.navLocations || 'Locations'
                    : translation.navResults || 'Results'}
                </Typography>
              </Stack>
              <Stack direction="row" gap={1}>
                {showSkeleton ? (
                  <>
                    <Skeleton variant="rounded" height={44} width={91} />
                    <Skeleton variant="rounded" height={44} width={138} />
                  </>
                ) : (
                  <>
                    {filtersEnabled && (
                      <FilterMenu
                        rtl={rtl}
                        menuItems={menuItems}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                      />
                    )}
                    {sortingEnabled && (
                      <SortMenu
                        rtl={rtl}
                        sortingValue={sortingValue}
                        setSortingValue={setSortingValue}
                      />
                    )}
                  </>
                )}
              </Stack>
            </Stack>
            <Stack id="items-wrapper" sx={itemsWrapperStyle}>
              {showSkeleton && (!categories?.length || hiddenFromLink) && (
                <>
                  <SkeletonPathItem />
                  <SkeletonPathItem />
                </>
              )}
              {!showLoader &&
                !showSkeleton &&
                filteredItems.map((item, i) => {
                  return (
                    <Stack
                      id={item?.id}
                      key={i}
                      sx={{ ...itemStyle, direction: rtl ? 'rtl' : '' }}
                      onClick={() => handleItemSelect(item)}
                    >
                      <Stack sx={itemInnerStyle}>
                        <Stack sx={itemIconStyle}>
                          {item?.tag?.webPathThumbnail && item?.tag?.navigationThumbnail ? (
                            <img
                              alt="Thumbnail"
                              src={`${process.env.NEXT_PUBLIC_CDN}/tours/${item.tourId}/tags/icons/${item.tag.navigationThumbnail}`}
                              style={{ width: '38px', height: '38px' }}
                            />
                          ) : (
                            renderIcon(item[item?.type] || item, 38, 38)
                          )}
                        </Stack>
                        <Stack
                          sx={{ textAlign: rtl ? 'right' : 'left', direction: rtl ? 'rtl' : 'ltr' }}
                        >
                          <Typography
                            sx={{
                              display: '-webkit-box',
                              width: { xs: 'unset', sm: '600px' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              lineHeight: '21.531px',
                              fontStyle: 'normal',
                              color: '#101828',
                              fontWeight: rtl ? 400 : 500,
                              fontSize: { xs: '14px', sm: '16px' },
                              wordBreak: 'break-word',
                              textAlign: {
                                xs: mobilePosition < 2 ? 'center' : 'unset',
                                sm: 'unset',
                              },
                              direction: rtl ? 'rtl' : 'ltr',
                            }}
                          >
                            {item[item.type]?.title || item?.title || item?.name}
                          </Typography>
                          <Typography
                            fontSize="15px"
                            fontWeight={400}
                            color="#667085"
                            lineHeight="21.531px"
                            fontStyle="normal"
                            sx={{ direction: rtl ? 'rtl' : 'ltr' }}
                          >
                            {trimText(item?.category?.title, 25)}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack sx={itemChevronStyle}>
                        <i className={`fa-solid fa-chevron-${rtl ? 'left' : 'right'}`} />
                      </Stack>
                    </Stack>
                  );
                })}
              {showLoader && (
                <Stack alignItems="center" sx={{ mb: 2 }}>
                  <Loader color="#2055FF" className="mx-4 my-4" />
                  <Typography
                    textAlign="center"
                    sx={{ color: '#2055FF', direction: rtl ? 'rtl' : 'ltr' }}
                  >
                    {translation?.navCalculatingPath10sec ||
                      'Calculating path... this may take 10 seconds'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        )}

      {DeviceManager.isMobile() &&
        filteredItems?.length > 3 &&
        !selectedCategory &&
        mobilePosition === 1 &&
        !popupOpen &&
        !choicePopupOpen &&
        !categories?.length && (
          <NavItemsSlider
            filteredItems={filteredItems}
            handleItemSelect={handleItemSelect}
            itemChevronStyle={itemChevronStyle}
            itemIconStyle={itemIconStyle}
            itemInnerStyle={itemInnerStyle}
            itemStyle={itemStyle}
            rtl={rtl}
            navigationSettings={navigationSettings}
            showSkeleton={showSkeleton}
          />
        )}
      {showModal && sharingEnabled && tourType === TourType.Kiosk && !isBackgroundVisible && (
        <FinishModal
          isOpen={showModal}
          inputClientRect={inputClientRect}
          ratingClientRect={ratingClientRect}
          target={target}
          popupTitle={popupTitle}
          qrEnabled={qrEnabled}
          qrTitle={qrTitle}
          smsEnabled={smsEnabled}
          smsTitle={smsTitle}
          smsText={smsText}
        />
      )}
      {tourType === TourType.Kiosk && (
        <NavigationCounterWrapper
          rtl={rtl}
          resetNavigation={resetNavigation}
          isBackgroundVisible={isBackgroundVisible}
          inAutoNavigate={inAutoNavigate}
        />
      )}
    </Stack>
  );
};
